import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Button, Stack, TextField, Chip, Alert, Divider,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { getAudit, AuditRow } from "../api";

const ROLES = ["Media", "MediaPoint", "Policy", "ViewingPolicy", "Audience", "Audit"];

function resultColor(r: string) {
  return r === "FAIL" ? "error" : r === "SUCCESS" ? "success" : "default";
}
function triggerColor(t: string) {
  if (t === "SIGNAL") return "secondary";
  if (t === "PUT" || t === "DELETE") return "warning";
  if (t === "GET" || t === "STATUS" || t === "TIME" || t === "DURATION") return "primary";
  return "default";
}

type Node = AuditRow & { children: Node[] };

function AuditNode({ n, depth }: { n: Node; depth: number }) {
  return (
    <Box>
      <Box sx={{ pl: depth * 3, py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          {depth > 0 && <Typography sx={{ opacity: 0.4, fontFamily: "monospace" }}>└─</Typography>}
          <Chip size="small" label={n.role || "?"} />
          <Chip size="small" color={triggerColor(n.trigger) as any} label={n.trigger} />
          {n.policyMode && <Chip size="small" variant="outlined" label={n.policyMode} />}
          <Chip size="small" color={resultColor(n.result) as any} variant="outlined" label={n.result} />
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, opacity: 0.85 }}>{n.href}</Typography>
        </Stack>
        <Typography variant="caption" sx={{ opacity: 0.65 }}>
          {n.lastUpdated}{n.authorization ? ` · ${n.authorization}` : ""}{n.description ? ` · ${n.description}` : ""}
        </Typography>
      </Box>
      {n.children.map((c) => <AuditNode key={c.id} n={c} depth={depth + 1} />)}
    </Box>
  );
}

export default function AuditViewer() {
  const [roles, setRoles] = useState<string[]>([]);
  const [limit, setLimit] = useState(100);
  const [updatedAfter, setUpdatedAfter] = useState("");
  const [updatedBefore, setUpdatedBefore] = useState("");
  const [size, setSize] = useState(0);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null); setBusy(true);
    try {
      const res = await getAudit({
        role: roles.length ? roles.join(",") : undefined,
        limit,
        updatedAfter: updatedAfter || undefined,
        updatedBefore: updatedBefore || undefined,
      });
      setSize(res.size);
      setRows(res.audits);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [roles, limit, updatedAfter, updatedBefore]);

  useEffect(() => { load(); }, []); // initial

  // Build the parent->child tree. Roots = audits with no parent in this page.
  const byId = new Map<string, Node>(rows.map((r): [string, Node] => [r.id, { ...r, children: [] }]));
  const roots: Node[] = [];
  Array.from(byId.values()).forEach((r) => {
    const parent = r.parentId ? byId.get(r.parentId) : undefined;
    if (parent) parent.children.push(r);
    else roots.push(r);
  });

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>Audit Log</Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
        The insert-only SCTE-224 audit record. Decision/status queries appear as a parent with its
        per-MediaPoint / ViewingPolicy / Audience evaluation children. Scoped to channels you can see.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack gap={1.5}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>role</Typography>
            <ToggleButtonGroup size="small" value={roles} onChange={(_, v) => setRoles(v)} sx={{ flexWrap: "wrap", display: "flex" }}>
              {ROLES.map((r) => <ToggleButton key={r} value={r}>{r}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2} flexWrap="wrap">
            <TextField size="small" type="number" label="limit" value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(5000, Number(e.target.value) || 100)))} sx={{ width: 110 }} />
            <TextField size="small" label="updatedAfter" value={updatedAfter} onChange={(e) => setUpdatedAfter(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField size="small" label="updatedBefore" value={updatedBefore} onChange={(e) => setUpdatedBefore(e.target.value)} sx={{ minWidth: 220 }} />
            <Button variant="contained" onClick={load} disabled={busy}>{busy ? "Loading…" : "Apply"}</Button>
          </Stack>
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Results</Typography>
        <Chip size="small" label={`size ${size}`} />
        <Typography variant="caption" sx={{ opacity: 0.6 }}>(total matching; showing {rows.length})</Typography>
      </Stack>
      <Divider />
      <Paper variant="outlined" sx={{ mt: 1 }}>
        {roots.length === 0 ? (
          <Typography sx={{ p: 2, opacity: 0.6 }}>no audits</Typography>
        ) : (
          roots.map((r) => <AuditNode key={r.id} n={r} depth={0} />)
        )}
      </Paper>
    </Box>
  );
}
